import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

export default class extends Controller {
  static targets = [ "messages" ]

  connect() {
    this.styleMessages()
    this.scrollToBottom()
    this.observeMessages()
    this.subscribe()
  }

  disconnect() {
    this.observer?.disconnect()
    this.unsubscribe()
  }

  subscribe() {
    this.subscription = consumer.subscriptions.create(
      { channel: "PresenceChannel" },
      {
        received: (data) => this.received(data)
      }
    )
  }

  unsubscribe() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
  }

  received(data) {
    if (data.type === "messages_read") {
      this.handleMessagesRead(data)
    }
  }

  observeMessages() {
    if (!this.hasMessagesTarget) return

    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE && node.classList.contains("chat-message")) {
            this.styleMessage(node)
            this.ensureReadCheck(node)
            this.resetTypingIndicator(node)
          }
        })
      })
      this.scrollToBottom()
      this.markIncomingAsRead()
    })
    this.observer.observe(this.messagesTarget, { childList: true, subtree: true })
  }

  styleMessages() {
    if (!this.hasMessagesTarget) return
    this.messagesTarget.querySelectorAll("[data-message-user-id]").forEach((node) => this.styleMessage(node))
  }

  styleMessage(node) {
    const currentUserId = this.messagesTarget.dataset.currentUserId
    const messageUserId = node.dataset.messageUserId
    if (!currentUserId || !messageUserId) return

    node.classList.remove("chat-message-own", "chat-message-other")
    if (messageUserId === currentUserId) {
      node.classList.add("chat-message-own")
    } else {
      node.classList.add("chat-message-other")
    }
  }

  ensureReadCheck(node) {
    const currentUserId = this.messagesTarget.dataset.currentUserId
    const messageUserId = node.dataset.messageUserId
    if (!currentUserId || messageUserId !== currentUserId) return

    const time = node.querySelector(".chat-time")
    if (!time || time.querySelector(".conversation-read-check")) return

    const readCheck = document.createElement("span")
    readCheck.className = "conversation-read-check"
    readCheck.setAttribute("aria-label", "Доставлено")
    readCheck.textContent = "✓"
    time.appendChild(readCheck)
  }

  handleMessagesRead(data) {
    if (!this.hasMessagesTarget) return

    const conversationId = parseInt(this.messagesTarget.dataset.conversationId, 10)
    if (!conversationId || data.conversation_id !== conversationId) return

    const readUpToId = data.read_up_to_id
    if (!readUpToId) return

    this.messagesTarget.querySelectorAll(".chat-message-own[data-message-id]").forEach((node) => {
      const messageId = parseInt(node.dataset.messageId, 10)
      if (messageId > readUpToId) return

      const check = node.querySelector(".conversation-read-check")
      if (!check || check.textContent.includes("✓✓")) return

      check.textContent = "✓✓"
      check.setAttribute("aria-label", "Прочитано")
      check.dataset.messageReadAt = new Date().toISOString()
    })
  }

  scrollToBottom() {
    if (this.hasMessagesTarget) {
      this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
    }
  }

  markIncomingAsRead() {
    if (!this.hasMessagesTarget) return

    const url = this.messagesTarget.dataset.markReadUrl
    if (!url) return
    if (!this.messagesTarget.querySelector(".chat-message-other")) return

    const token = document.querySelector("meta[name='csrf-token']")?.content

    fetch(url, {
      method: "PATCH",
      headers: {
        "X-CSRF-Token": token,
        "Accept": "text/vnd.turbo-stream.html, text/html, application/xhtml+xml"
      },
      credentials: "same-origin"
    })
  }

  resetTypingIndicator(_node) {
    if (!this.hasMessagesTarget) return

    const conversationId = this.messagesTarget.dataset.conversationId
    if (!conversationId) return

    document.dispatchEvent(new CustomEvent("conversation:typing:reset", {
      detail: { conversation_id: parseInt(conversationId, 10) }
    }))
  }
}
